import { ReactNode, useEffect } from "react";
import { Mail, Phone, MapPin } from "lucide-react";

const LAST_UPDATED = "18 August 2026";

interface PolicySection {
  id: string;
  title: string;
  body: ReactNode;
}

/* Small helpers so every section reads the same */
const P = ({ children }: { children: ReactNode }) => (
  <p className="text-body text-gray-700">{children}</p>
);

const Bullets = ({ items }: { items: ReactNode[] }) => (
  <ul className="flex flex-col gap-2 pl-5 list-disc marker:text-primary/60">
    {items.map((item, i) => (
      <li key={i} className="text-body text-gray-700">
        {item}
      </li>
    ))}
  </ul>
);

const Strong = ({ children }: { children: ReactNode }) => (
  <span className="font-semibold text-primary">{children}</span>
);

const SECTIONS: PolicySection[] = [
  {
    id: "introduction",
    title: "1. Introduction",
    body: (
      <>
        <P>
          Sacrum 3D (&quot;Sacrum 3D&quot;, &quot;we&quot;, &quot;us&quot; or
          &quot;our&quot;) designs and manufactures custom 3D printed medical
          casts and related orthopaedic products. This Privacy Policy explains
          what personal data we collect through this website, why we collect
          it, how we use and protect it, and the choices you have.
        </P>
        <P>
          By using this website or submitting an enquiry through it, you agree
          to the practices described here. If you do not agree, please do not
          submit personal information through the site.
        </P>
        <P>
          This policy applies to{" "}
          <Strong>www.sacrum3d.com and its sub-pages</Strong>. It does not
          cover our customer portal, clinical partners, or any third-party site
          you reach from a link here, each of which has its own terms.
        </P>
      </>
    ),
  },
  {
    id: "information-we-collect",
    title: "2. Information We Collect",
    body: (
      <>
        <P>
          <Strong>Information you give us.</Strong> When you fill in a contact
          or enquiry form on this website, we collect:
        </P>
        <Bullets
          items={[
            "Your full name",
            "Your email address",
            "Your phone number",
            "Whether you are contacting us as a patient, doctor or partner",
            "The contents of the message you write to us",
          ]}
        />
        <P>
          <Strong>Health-related information.</Strong> If you choose to
          describe an injury, a diagnosis, a prescription or a clinical
          requirement in your message, that information is treated as sensitive
          and is used only to respond to your enquiry and, where relevant, to
          assess whether our products are suitable for you. Please share only
          what is necessary — this website is not a clinical intake system and
          should not be used to send medical records.
        </P>
        <P>
          <Strong>Technical information.</Strong> Like most websites, our
          hosting infrastructure automatically records standard request data
          such as IP address, browser type, device type, referring page and
          time of access. This is used for security, diagnostics and to keep
          the site working correctly.
        </P>
        <P>
          We do not run advertising trackers, and we do not build marketing
          profiles from your visit.
        </P>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "3. How We Use Your Information",
    body: (
      <>
        <P>We use the information described above to:</P>
        <Bullets
          items={[
            "Respond to your enquiry, quotation request or support question",
            "Assess whether a Sacrum 3D product is suitable for your requirement and advise accordingly",
            "Coordinate with a treating clinician, hospital or partner where you have asked us to",
            "Arrange scanning, fitting, delivery and after-care for an order",
            "Maintain records required for tax, warranty, product-safety and other statutory obligations",
            "Keep the website secure, prevent misuse and diagnose technical problems",
            "Improve our products and services in aggregate, non-identifying form",
          ]}
        />
        <P>
          We will not use your contact details to send you marketing messages
          unrelated to your enquiry unless you have asked us to.
        </P>
      </>
    ),
  },
  {
    id: "legal-basis",
    title: "4. Consent and Legal Basis",
    body: (
      <>
        <P>
          We process personal data on the basis of the consent you give when
          you voluntarily submit a form or contact us, and, where applicable,
          for legitimate uses and legal obligations recognised under the{" "}
          <Strong>Digital Personal Data Protection Act, 2023</Strong> and other
          applicable Indian law.
        </P>
        <P>
          Your consent is specific to the purpose for which you gave the
          information. You may withdraw it at any time by writing to us at the
          address in Section 14, and we will stop processing your data for that
          purpose, subject to records we are legally required to retain.
        </P>
      </>
    ),
  },
  {
    id: "cookies",
    title: "5. Cookies and Similar Technologies",
    body: (
      <>
        <P>
          Sacrum 3D does not set advertising or profiling cookies on this
          website. Your browser may store a small amount of technical data
          needed to load the site — for example, cached 3D model files and
          images — so that pages open faster on your next visit.
        </P>
        <P>
          Content embedded from third parties (see Section 6) may set its own
          cookies once you interact with it. You can clear or block cookies
          through your browser settings; blocking them may stop embedded videos
          and forms from working.
        </P>
      </>
    ),
  },
  {
    id: "third-parties",
    title: "6. Third-Party Services and Embedded Content",
    body: (
      <>
        <P>
          Parts of this website rely on services operated by other companies.
          When you interact with them, their own privacy policies apply in
          addition to ours:
        </P>
        <Bullets
          items={[
            <>
              <Strong>YouTube</Strong> — product and testimonial videos are
              embedded from YouTube. Playing a video allows YouTube to receive
              your IP address and set cookies.
            </>,
            <>
              <Strong>Google Forms</Strong> — some enquiry forms are hosted by
              Google. Responses you submit there are received by Google on our
              behalf.
            </>,
            <>
              <Strong>Google Maps</Strong> — our address links out to Google
              Maps. Following that link takes you to a Google-operated page.
            </>,
            <>
              <Strong>Sacrum 3D portal and enquiry systems</Strong> — enquiries
              submitted on this site are transmitted over an encrypted
              connection to our own systems for handling by our team.
            </>,
            <>
              <Strong>Hosting and delivery providers</Strong> — the
              infrastructure that serves this website processes request data on
              our instructions.
            </>,
          ]}
        />
        <P>
          We select service providers that are contractually or otherwise
          obliged to protect your data and to use it only for the purposes we
          specify.
        </P>
      </>
    ),
  },
  {
    id: "sharing",
    title: "7. Sharing and Disclosure",
    body: (
      <>
        <P>
          <Strong>We do not sell or rent your personal data.</Strong> We share
          it only in these situations:
        </P>
        <Bullets
          items={[
            "With clinicians, hospitals, distributors or fitment partners where this is necessary to answer your enquiry or fulfil your order, and where you have asked for or agreed to that coordination",
            "With service providers who host our systems, deliver our products or process payments, strictly to perform those services for us",
            "Where disclosure is required by law, by a court, or by a regulatory or government authority, including medical-device and product-safety reporting obligations",
            "To establish, exercise or defend legal claims, or to protect the rights and safety of our users, our staff or the public",
            "To a successor entity in the event of a merger, acquisition or reorganisation, with the same protections continuing to apply",
          ]}
        />
      </>
    ),
  },
  {
    id: "security",
    title: "8. Data Security",
    body: (
      <P>
        Enquiries submitted through this website are transmitted over encrypted
        (HTTPS) connections. Access to enquiry data inside Sacrum 3D is limited
        to the staff who need it to respond to you, and our systems are
        protected by authentication and access controls. No method of
        transmission or storage is completely secure, so while we work to
        protect your information we cannot guarantee absolute security. If a
        personal data breach occurs, we will notify affected individuals and
        the Data Protection Board as required by law.
      </P>
    ),
  },
  {
    id: "retention",
    title: "9. How Long We Keep Your Data",
    body: (
      <P>
        We keep enquiry data for as long as needed to deal with your request
        and to maintain a reasonable record of our dealings with you. Where an
        enquiry leads to an order, related records are kept for the periods
        required by tax, warranty, medical-device and product-liability rules.
        When data is no longer needed for any of these purposes, and no legal
        requirement to keep it applies, it is deleted or anonymised.
      </P>
    ),
  },
  {
    id: "your-rights",
    title: "10. Your Rights",
    body: (
      <>
        <P>
          Subject to applicable law, you have the right to:
        </P>
        <Bullets
          items={[
            "Ask what personal data of yours we hold and how it has been processed",
            "Ask us to correct data that is inaccurate, incomplete or out of date",
            "Ask us to erase data that is no longer needed for the purpose it was collected for",
            "Withdraw a consent you previously gave, at any time",
            "Nominate another person to exercise these rights on your behalf in the event of your death or incapacity",
            "Raise a grievance with us about how your data has been handled",
          ]}
        />
        <P>
          To exercise any of these rights, contact us using the details in
          Section 14. We may need to verify your identity before acting on a
          request, and we will respond within a reasonable period. If you are
          not satisfied with our response, you may escalate the matter to the
          Data Protection Board of India.
        </P>
      </>
    ),
  },
  {
    id: "children",
    title: "11. Children",
    body: (
      <P>
        Sacrum 3D products are used by patients of all ages, including
        children, but this website is not directed at children. Where an
        enquiry concerns a person under 18 years of age, it must be submitted
        by a parent or lawful guardian, whose consent we rely on for processing
        that information. We do not knowingly collect personal data directly
        from a child, and we do not undertake tracking, behavioural monitoring
        or targeted advertising directed at children. If you believe a child
        has submitted information to us without guardian consent, write to us
        and we will delete it.
      </P>
    ),
  },
  {
    id: "external-links",
    title: "12. Links to Other Sites",
    body: (
      <P>
        This website links to external sites, including our social media
        profiles, our customer portal and mapping services. We are not
        responsible for the content or privacy practices of sites we do not
        operate. Please review the privacy policy of any site you visit through
        a link from here.
      </P>
    ),
  },
  {
    id: "changes",
    title: "13. Changes to This Policy",
    body: (
      <P>
        We may update this Privacy Policy as our services, systems or legal
        obligations change. The revised version will be posted on this page
        with a new &quot;Last updated&quot; date. Where a change materially
        affects how we handle data you have already given us, we will take
        reasonable steps to inform you. Please check this page from time to
        time.
      </P>
    ),
  },
];

const CONTACT_ITEMS = [
  {
    icon: Mail,
    label: "info@sacrum3d.com",
    href: "mailto:info@sacrum3d.com",
  },
  {
    icon: Phone,
    label: "+91 8830540638",
    href: "tel:+918830540638",
  },
  {
    icon: MapPin,
    label:
      "35, OmBunglow, Phatak Baug, Navi Peth, Sadashiv Peth, Pune, Maharashtra 411030",
    href: "https://www.google.com/maps?q=35, OmBunglow, Phatak Baug, Navi Peth, Sadashiv Peth, Pune, Maharashtra 411030",
  },
];

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jumpTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <section className="relative overflow-hidden pt-6 pb-20">
      {/* 🌈 BACKGROUND DECOR */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute bottom-0 right-10 h-[300px] w-[300px] rounded-full bg-accent/20 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-col items-center gap-12">
        {/* 🧠 HEADER */}
        <div className="text-center max-w-3xl">
          <h1 className="text-3xl sm:text-4xl xl:text-5xl font-semibold tracking-tight text-primary">
            Privacy Policy
          </h1>
          <p className="mt-4 text-body text-gray-600">
            How Sacrum 3D collects, uses and protects the information you share
            with us.
          </p>
          <p className="mt-2 text-xs uppercase tracking-widest text-gray-500">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <div className="w-full lg:w-11/12 xl:w-5/6 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10 items-start">
          {/* 📑 CONTENTS */}
          <nav
            aria-label="Privacy policy contents"
            className="hidden lg:block sticky top-24 bg-white/70 border border-primary/20 rounded-xl p-5"
          >
            <p className="font-semibold text-primary text-sm mb-3">Contents</p>
            <ul className="flex flex-col gap-2">
              {SECTIONS.map((section) => (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => jumpTo(section.id)}
                    className="text-left text-sm text-gray-600 hover:text-primary transition-colors"
                  >
                    {section.title}
                  </button>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={() => jumpTo("contact")}
                  className="text-left text-sm text-gray-600 hover:text-primary transition-colors"
                >
                  14. Contact Us
                </button>
              </li>
            </ul>
          </nav>

          {/* 📝 POLICY BODY */}
          <div className="w-full bg-white border border-primary/20 rounded-xl p-6 sm:p-10 flex flex-col gap-10">
            {SECTIONS.map((section) => (
              <article
                key={section.id}
                id={section.id}
                className="scroll-mt-28 flex flex-col gap-3"
              >
                <h2 className="text-lg sm:text-xl font-semibold text-primary">
                  {section.title}
                </h2>
                {section.body}
              </article>
            ))}

            {/* ☎️ CONTACT */}
            <article id="contact" className="scroll-mt-28 flex flex-col gap-4">
              <h2 className="text-lg sm:text-xl font-semibold text-primary">
                14. Contact Us
              </h2>
              <P>
                For any question, request or grievance relating to your
                personal data, or to reach our Grievance Officer, contact us at:
              </P>

              <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 flex flex-col gap-3">
                {CONTACT_ITEMS.map(({ icon: Icon, label, href }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={
                      href.startsWith("http") ? "noopener noreferrer" : undefined
                    }
                    className="flex items-start gap-3 text-sm text-gray-700 hover:text-primary transition-colors"
                  >
                    <Icon size={16} className="mt-1 shrink-0 text-primary" />
                    <span>{label}</span>
                  </a>
                ))}
              </div>

              <p className="text-xs text-gray-500">
                Sacrum 3D · CIN U28299PN2023PTC222338 · GST 27ABLCS2758L1ZN
              </p>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
